// src/publish/piece.js — the publishable piece: shape, i18n lookup, shared limits.
//
// A piece is what lands in ONE box of the destination platform (the caption,
// the video title, the meta description, the email subject). Every per-format
// builder in pieces/ produces a list of them through makePiece():
//
//   { id, label, destination, primary: boolean,
//     kind: 'plain' | 'rich' | 'tags' | 'url' | 'list' | 'source',
//     blocks: blocks[], limit?: { max, visible? } }
//
//   kind 'plain'  text pasted as is (social post, subject, title)
//        'rich'   headings / bold / lists matter (article, email body, page)
//        'tags'   one paragraph "#a #b" (or "a, b" for YouTube tags)
//        'url'    one paragraph holding a single link run — or, for a
//                 site-relative path such as the blog's "/slug", a single literal
//                 text run: the box it goes to takes an address, not a link
//        'list'   one list block (or qa blocks for a FAQ)
//        'source' a verbatim code source (the blog's body_html): plain text
//                 skips it, Markdown fences it, HTML shows it in <pre>
//
// Label and destination are user-facing and come from i18n — never from the
// backend. Key convention (outputs namespace, "pieces" object):
//   label        outputs:pieces.labels.<format>.<id>   (per-format override)
//                outputs:pieces.<id>                   (shared fallback)
//   destination  outputs:pieces.destinations.<format>.<id>
//                outputs:pieces.destinations.<id>      (shared fallback)
// <format> is entryFormatKey(entry) = "<platform>-<contentFormat>", e.g.
// "linkedin-post", "blog-seo", "email-email-classic"; the generic builder uses
// "generic". A piece whose label takes a parameter (the carousel's "Slide {{n}}")
// passes labelId + labelParams; the destination of a repeated piece passes
// destinationId. A missing label falls back to the id so nothing ever renders
// a raw key.
//
// Also here, for every builder: the backend-field coercions (str / trimmed /
// arr / obj — a wrong type is an empty value, never a throw) and
// ensureOnePrimary, the one rule that exactly one piece of a list is primary.

import i18n from '../i18n/index.js';
import { normalizeUrl } from '../utils/textProcessing.js';
import { paragraph, link, list, text, isBlock } from './blocks.js';

export const PIECE_KINDS = ['plain', 'rich', 'tags', 'url', 'list', 'source'];

// ─── Backend-field coercions ─────────────────────────────────────────────────

/** The string itself, '' for anything else. */
export const str = (v) => (typeof v === 'string' ? v : '');
/** The string trimmed, '' for anything else. */
export const trimmed = (v) => (typeof v === 'string' ? v.trim() : '');
/** The array itself, [] for anything else. */
export const arr = (v) => (Array.isArray(v) ? v : []);
/** The plain object itself, {} for anything else (an array is not an object here). */
export const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/**
 * Exactly one primary: when no piece is primary yet, the first piece whose id
 * is in `preferredIds` (in that order) becomes primary, else the first piece.
 * Returns the list for chaining; an empty list stays empty.
 */
export function ensureOnePrimary(pieces, preferredIds = []) {
  const list = Array.isArray(pieces) ? pieces : [];
  if (!list.length || list.some((p) => p && p.primary)) return list;
  const primary = preferredIds.map((id) => list.find((p) => p && p.id === id)).find(Boolean) || list[0];
  if (primary) primary.primary = true;
  return list;
}

/** Character limits named by the backend's rendering docs (counters only; nothing is cut). */
export const LIMITS = Object.freeze({
  linkedinPost: { max: 3000 },
  facebookPost: { max: 2000, visible: 477 },
  instagramCaption: { max: 2200, visible: 125 },
  pinterestDescription: { max: 500 },
  pinterestTitle: { max: 100 },
  youtubeDescription: { max: 5000 },
  youtubeTitle: { max: 100 },
  blogMetaTitle: { max: 60 },
  blogMetaDescription: { max: 160 },
  emailSubject: { max: 60 },
});

/** "<platform>-<contentFormat>" of a history entry, 'generic' when either is missing. */
export function entryFormatKey(entry) {
  const r = entry?.results;
  const platform = typeof r?.platform === 'string' ? r.platform.trim() : '';
  const cf = typeof r?.contentFormat === 'string' ? r.contentFormat.trim() : '';
  return platform && cf ? `${platform}-${cf}` : 'generic';
}

function tFirst(keys, params) {
  for (const k of keys) {
    if (i18n.exists(k)) return i18n.t(k, params);
  }
  return null;
}

/** The label of a piece id for a format (same lookup makePiece uses). */
export function pieceLabel(format, id, { labelId, labelParams } = {}) {
  const key = labelId || id;
  return tFirst([
    `outputs:pieces.labels.${format}.${key}`,
    `outputs:pieces.${key}`,
  ], labelParams) ?? String(id);
}

/** The destination line of a piece id for a format ('' when none is defined). */
export function pieceDestination(format, id, { destinationId } = {}) {
  const key = destinationId || id;
  return tFirst([
    `outputs:pieces.destinations.${format}.${key}`,
    `outputs:pieces.destinations.${key}`,
  ]) ?? '';
}

/**
 * Builds one piece. `format` is entryFormatKey(entry); `blocks` may be a block
 * list or a single block. An unknown kind becomes 'plain'; a limit without a
 * positive `max` is dropped.
 */
export function makePiece(format, {
  id, primary = false, kind = 'plain', blocks = [], limit = null,
  labelId, labelParams, destinationId,
} = {}) {
  const blockList = (Array.isArray(blocks) ? blocks : [blocks]).filter(isBlock);
  const piece = {
    id: String(id),
    label: pieceLabel(format, id, { labelId, labelParams }),
    destination: pieceDestination(format, id, { destinationId }),
    primary: Boolean(primary),
    kind: PIECE_KINDS.includes(kind) ? kind : 'plain',
    blocks: blockList,
  };
  if (limit && Number.isFinite(Number(limit.max)) && Number(limit.max) > 0) {
    piece.limit = { max: Number(limit.max) };
    if (Number.isFinite(Number(limit.visible)) && Number(limit.visible) > 0) piece.limit.visible = Number(limit.visible);
  }
  return piece;
}

// ─── Block helpers shared by the builders ────────────────────────────────────

/** The brief's CTA URL: content.cta_url, else params.cta_url, normalised; '' when none. */
export function ctaUrlOf(entry) {
  const content = entry?.results?.content;
  const fromContent = content && typeof content === 'object' ? content.cta_url : null;
  const fromParams = entry?.params?.cta_url ?? entry?.params?.ctaUrl;
  return normalizeUrl(typeof fromContent === 'string' && fromContent.trim() ? fromContent : fromParams);
}

/** Hashtags → the one "#a #b" paragraph of a 'tags' piece; [] when there are none. */
export function tagsBlocks(hashtags) {
  const tags = (Array.isArray(hashtags) ? hashtags : [])
    .filter((t) => typeof t === 'string' && t.trim())
    .map((t) => t.trim())
    .map((t) => (t.startsWith('#') ? t : `#${t}`));
  return tags.length ? [paragraph([text(tags.join(' '))])] : [];
}

/** A URL → the one paragraph with a single link run of a 'url' piece; [] when empty. */
export function urlBlocks(url, label) {
  const href = normalizeUrl(url);
  if (!href) return [];
  return [paragraph([link(href, [text(label || href)])])];
}

/** Literal strings → the one list block of a 'list' piece; [] when there are none. */
export function listBlocks(items, ordered = false) {
  const strings = (Array.isArray(items) ? items : [])
    .filter((it) => typeof it === 'string' && it.trim())
    .map((it) => it.trim());
  return strings.length ? [list(strings, ordered)] : [];
}
